
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateResponse} from './ConfigUpdateResponse';
import {FieldModelConfigUpdateResponse} from './ConfigUpdateResponse';
import {CommissionProfile} from '../core/CommissionProfile';
import {FieldModelCommissionProfile} from '../core/CommissionProfile';

/**
 * CommissionProfileModifyResponse struct
 */
class CommissionProfileModifyResponse extends ConfigUpdateResponse {
  /**
   * Initialize struct
   * @param {!ConfigUpdateResponse=} parent
   * @param {!CommissionProfile=} commissionProfile
   * @constructor
   */
  constructor (parent = new ConfigUpdateResponse(), commissionProfile = new CommissionProfile()) {
    super()
    super.copy(parent)
    this.CommissionProfile = commissionProfile
  }

  /**
   * Copy struct (shallow copy)
   * @this {!CommissionProfileModifyResponse}
   * @param {!CommissionProfileModifyResponse} other Other struct
   * @returns {!CommissionProfileModifyResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.CommissionProfile != null) {
      this.CommissionProfile = CommissionProfile.fromObject(other.CommissionProfile)
    } else {
      this.CommissionProfile = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!CommissionProfileModifyResponse}
   * @returns {!CommissionProfileModifyResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new CommissionProfileModifyResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new CommissionProfileModifyResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!CommissionProfileModifyResponse}
   * @param {!CommissionProfileModifyResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof CommissionProfileModifyResponse)) {
      throw new TypeError('Instance of CommissionProfileModifyResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!CommissionProfileModifyResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      CommissionProfile: ((this.CommissionProfile != null) ? this.CommissionProfile : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return CommissionProfileModifyResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!CommissionProfileModifyResponse} other Object value
   * @returns {!CommissionProfileModifyResponse} Created struct
   */
  static fromObject (other) {
    return new CommissionProfileModifyResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!CommissionProfileModifyResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return CommissionProfileModifyResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!CommissionProfileModifyResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6129
  }
}

export { CommissionProfileModifyResponse };

/**
 * Fast Binary Encoding CommissionProfileModifyResponse field model
 */
class FieldModelCommissionProfileModifyResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateResponse(buffer, 4 + 4)
    this.CommissionProfile = new FieldModelCommissionProfile(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the ConfigUpdateResponse field model
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @returns {!FieldModelConfigUpdateResponse} ConfigUpdateResponse field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.CommissionProfile.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.CommissionProfile.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelCommissionProfileModifyResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6129
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CommissionProfile.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CommissionProfile.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfile.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @param {!CommissionProfileModifyResponse} fbeValue Default value, defaults is new CommissionProfileModifyResponse()
   * @returns {!CommissionProfileModifyResponse} CommissionProfileModifyResponse value
   */
  get (fbeValue = new CommissionProfileModifyResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @param {!CommissionProfileModifyResponse} fbeValue CommissionProfileModifyResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.CommissionProfile.fbeSize) <= fbeStructSize) {
      fbeValue.CommissionProfile = this.CommissionProfile.get()
    } else {
      fbeValue.CommissionProfile = new CommissionProfile()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CommissionProfile.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @param {!CommissionProfileModifyResponse} fbeValue CommissionProfileModifyResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelCommissionProfileModifyResponse}
   * @param {CommissionProfileModifyResponse} fbeValue CommissionProfileModifyResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.CommissionProfile.set(fbeValue.CommissionProfile)
  }
}

export { FieldModelCommissionProfileModifyResponse };

/**
 * Fast Binary Encoding CommissionProfileModifyResponse model
 */
class CommissionProfileModifyResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelCommissionProfileModifyResponse(this.buffer, 4)
  }

  /**
   * Get the CommissionProfileModifyResponse model
   * @this {!CommissionProfileModifyResponseModel}
   * @returns {!FieldModelCommissionProfileModifyResponse} model CommissionProfileModifyResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!CommissionProfileModifyResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!CommissionProfileModifyResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return CommissionProfileModifyResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!CommissionProfileModifyResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelCommissionProfileModifyResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!CommissionProfileModifyResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!CommissionProfileModifyResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!CommissionProfileModifyResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!CommissionProfileModifyResponseModel}
   * @param {!CommissionProfileModifyResponse} value CommissionProfileModifyResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!CommissionProfileModifyResponseModel}
   * @param {!CommissionProfileModifyResponse} value CommissionProfileModifyResponse value, defaults is new CommissionProfileModifyResponse()
   * @return {!object} Deserialized CommissionProfileModifyResponse value and its size
   */
  deserialize (value = new CommissionProfileModifyResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new CommissionProfileModifyResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new CommissionProfileModifyResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!CommissionProfileModifyResponseModel}
   * @param {!number} prev Previous CommissionProfileModifyResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { CommissionProfileModifyResponseModel };
